import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  ElementRef,
  ViewChild,
  SimpleChanges,
  OnDestroy,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-highlight-chart',
  imports: [CommonModule],
  templateUrl: './highlight-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./highlight-chart.component.scss'],
})
export class HighlightChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() chartData: any[][] = [];
  @Input() formatCurrency: boolean = true;
  @Input() marginProps: any;
  @Input() chartType: 'pie' | 'bar' | 'multiLine' | 'lineChart' = 'multiLine';
  @Input() width: any = 928;
  @Input() height: any = 300;
  @Input() createYGrid: boolean = true;
  @Input() yLabels: boolean = true;
  @Input() circleStroke: boolean = true;
  @Input() mainLineColor: any = null;
  @Input() showXaxis: boolean = false;
  @Input() valueSelector: any = null;
  @Input() unit: 'day' | 'month' | 'year' | 'weekly' | any = null;
  @Input() currentPeriod: string = '';
  @Input() previousPeriod: string = '';

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
  resizeObv: Map<Element, ResizeObserver> = new Map();

  chartId: string = `chart-${Math.floor(Math.random() * 1000000)}`;
  
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private themeService = inject(ThemeService);
  ngAfterViewInit() {
    // Subscribe to theme changes
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.drawChart(); // Redraw chart with new theme colors
    });

    if (this.chartData?.length) {
      const container = this.getInnerChartContainer();
      this.width = container.clientWidth;
      this.height = container.clientHeight;

      if (typeof this.marginProps == 'string') {
        this.marginProps = JSON.parse(this.marginProps);
      }
      this.drawChart();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chartData'] || changes['currentPeriod'] || changes['previousPeriod']) {
      Promise.resolve().then(() => this.drawChart());
    }
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    // Disconnect all resize observers
    this.resizeObv.forEach(observer => observer.disconnect());
    this.resizeObv.clear();
  }

  // Get theme-aware colors
  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      // Line colors
      primaryLine: this.mainLineColor || (isDark ? '#60a5fa' : '#1581E5'), // blue-400 / custom blue
      secondaryLine: isDark ? '#a1a1aa' : '#c4cbd5', // slate-400 / light gray
      circleStroke: isDark ? '#60a5fa' : '#1581E5', // blue-400 / custom blue
      circleFill: isDark ? '#1e1e21' : '#fff', // slate-800 / white
      
      // Grid and axis colors
      gridLine: isDark ? '#2e2e33' : '#F2F5F8', // slate-700 / light gray
      axisLine: isDark ? '#85858e' : '#DFE5ED', // slate-500 / gray
      axisText: isDark ? '#d4d4d8' : '#4A4A4A', // slate-300 / dark gray
      yAxisText: isDark ? '#d4d4d8' : '#131921', // slate-300 / dark
      
      // Tooltip colors
      tooltipBg: isDark ? '#1e1e21' : '#FFFFFF', // slate-800 / white
      tooltipBorder: isDark ? '#47474e' : '#e8e8e8', // slate-600 / light gray
      tooltipText: isDark ? '#f4f4f5' : '#131921', // slate-100 / dark
      tooltipShadow: isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(16, 24, 40, 0.2)',
      
      // Bar chart colors (for lineChart type)
      barCurrentPeriod: isDark ? '#818cf8' : '#6366f1', // indigo-400 / indigo-500
      barPreviousPeriod: isDark ? '#a78bfa' : '#8b5cf6', // purple-400 / purple-600
      barEmpty: isDark ? '#47474e' : '#CBD1D9', // slate-600 / light gray
      barLabelBg: isDark ? '#1e1e21' : '#fff', // slate-800 / white
      barLabelText: isDark ? '#f4f4f5' : '#1a1a1a', // slate-100 / dark
      barLabelShadow: isDark ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)',
      
      // Pie chart colors - keep vibrant for both themes
      pieColors: [
        isDark ? '#60a5fa' : '#97C9F3',
        isDark ? '#5eead4' : '#9BEBED',
        isDark ? '#bef264' : '#DFF3B5',
        isDark ? '#fdba74' : '#F3D7BF',
        isDark ? '#fb7185' : '#F3B1AD',
        isDark ? '#c084fc' : '#AD9BD5',
        isDark ? '#4ade80' : '#8DDFAF',
        isDark ? '#7dd3fc' : '#A5CBEF',
        isDark ? '#5eead4' : '#B3E6E1',
        isDark ? '#bef264' : '#CBEF9E',
        isDark ? '#fdba74' : '#F5CBAE',
        isDark ? '#fb7185' : '#F9A6A6',
        isDark ? '#d8b4fe' : '#C3B3E5',
        isDark ? '#86efac' : '#A6EFC1',
      ],
      pieText: isDark ? '#f4f4f5' : '#000000', // slate-100 / black
      
      // Hover line color
      hoverLine: isDark ? '#85858e' : '#A2A8B0', // slate-500 / gray
    };
  }

  private drawChart() {
    try {
      const container = this.getInnerChartContainer();

      if (!container) return;

      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }

      if (
        !this.chartData ||
        !this.chartData.length ||
        (this.chartData.every((arr) => !arr.length) &&
          this.chartType != 'lineChart')
      ) {
        return;
      }

      // Call the appropriate chart draw method
      switch (this.chartType) {
        case 'multiLine':
          this.drawMultiLineChart(container);
          break;
        case 'bar':
          this.drawBarChart(container);
          break;
        case 'pie':
          this.drawPieChart(container);
          break;
        case 'lineChart':
          this.createLineChart(container);
          break;
        default:
          console.warn('Unsupported chart type:', this.chartType);
      }
    } catch (error) {
      console.error(error);
    }
  }

  private drawPieChart(container: HTMLElement) {
    try {
      if (this.resizeObv.has(container)) {
        this.resizeObv.get(container)?.disconnect();
        this.resizeObv.delete(container);
      }

      // Get theme colors
      const colors = this.getThemeColors();

      const width = container.clientWidth;
      const height = container.clientHeight;
      const radius = Math.min(width, height) / 2;

      const data = this.chartData[0].filter((d) => d?.value > 0) || [];

      const svg = d3
        .select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .attr('padding-left', 10)
        .append('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`);

      // const pie = d3.pie<any>().value(d => d.value);
      const pie = d3
        .pie()
        .padAngle(1 / radius)
        .sort(null)
        .value((d: any) => d.value);

      const arc = d3
        .arc<any>()
        .innerRadius(radius * 0.45)
        .outerRadius(radius - 20);

      // Assign different colors to each portion by index
      const color = d3
        .scaleOrdinal<string>()
        .domain(data.map((_, i) => i.toString()))
        .range(data.map((_, i) => colors.pieColors[i % colors.pieColors.length]));

      svg
        .append('g')
        .selectAll()
        .data(pie(data))
        .join('path')
        .attr('fill', (d: any) => d.color || color(d.data.name))
        .attr('d', arc)
        .append('title')
        .text((d: any) => `${d.data.name}: ${d.data.value.toLocaleString()}`);

      svg
        .append('g')
        .attr('font-family', 'sans-serif')
        .attr('font-size', 12)
        .attr('text-anchor', 'middle')
        .selectAll()
        .data(pie(data))
        .join('text')
        .attr('transform', (d) => `translate(${arc.centroid(d)})`)
        .attr('fill', colors.pieText)
        .call((text) =>
          text
            .append('tspan')
            .attr('y', '-0.4em')
            .attr('font-weight', 'bold')
            .text((d: any) => d.data.name)
        )
        .call((text) =>
          text
            .filter((d: any) => d.endAngle - d.startAngle > 0.25)
            .append('tspan')
            .attr('x', 0)
            .attr('y', '0.7em')
            .attr('fill-opacity', 0.7)
            .text((d: any) => d.data.value.toLocaleString('en-US'))
        );

      // let lastWidth = container.clientWidth;
      // let lastHeight = container.clientHeight;

      // const observer = new ResizeObserver(() => {
      //   const { clientWidth, clientHeight } = container;
      //   if (clientWidth !== lastWidth || clientHeight !== lastHeight) {
      //     lastWidth = clientWidth;
      //     lastHeight = clientHeight;
      //     this.drawPieChart(container);
      //   }
      // });

      // observer.observe(container);
      // this.resizeObv.set(container, observer);
    } catch (error) {
      console.error(error);
    }
  }

  private drawMultiLineChart(container: HTMLElement) {
    if (!container) return;

    // Cleanup
    while (container.firstChild) container.removeChild(container.firstChild);

    if (this.resizeObv.has(container)) {
      this.resizeObv.get(container)?.disconnect();
      this.resizeObv.delete(container);
    }

    // Get theme colors
    const colors = this.getThemeColors();

    const dataSets = this.chartData.filter((d) => d.length > 0);

    if (!dataSets.length) return;

    const parser = this.formatTimestamp(this.unit || 'day');
    const data = dataSets.map((dm, index) => {
      const uColor = index === 0 ? colors.primaryLine : colors.secondaryLine;
      const color =
        index === 0
          ? uColor
          : this.chartData.length === 2
          ? colors.secondaryLine
          : colors.pieColors[index % colors.pieColors.length];
      return dm.map((ds) => ({
        start_time: new Date(ds.start),
        end_time: new Date(ds.end),
        label: ds?.label || parser(new Date(ds.start)) || '',
        value: this.valueSelector
          ? parseFloat(ds[this.valueSelector].toFixed(2))
          : parseFloat(ds.value.toFixed(2)),
        color,
        tooltip: ds.tooltip.includes('box_color')
          ? ds.tooltip.replace('box_color', color)
          : ds.tooltip,
      }));
    });

    const mainData = data[1] || data[0] || [];
    const values = mainData.map((d) => d.value);
    const margin = this.marginProps || {
      top: 30,
      right: 30,
      bottom: 50,
      left: Math.max(...values) > 100 ? 60 : 50,
    };

    const width = container.clientWidth + 50;
    const height = container.clientHeight + 30;

    const x = d3
      .scaleUtc()
      .domain(d3.extent(mainData, (d, i) => d.start_time) as [Date, Date])
      .range([30, width - 30]);

    const formatDate = d3.utcFormat('%b %d, %Y'); // "Jul 18", "Oct 03", etc.

    const rawMax = d3.max(data.flat(), (d) => d.value) || 0;
    const step = this.getStepSize(rawMax);
    const yMax = Math.ceil(rawMax / step) * step;

    const y = d3
      .scaleLinear()
      .domain([0, yMax])
      // .range([height, 0])
      .nice()
      .range([height - 30, 0]);

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('transform', `translate(-25, 0)`);

    //    // Append axis to SVG
    // svg.append("g")
    //   .attr("transform", `translate(0,${height - margin.bottom})`)
    //   .call(xAxis);

    // Y Grid
    if (this.createYGrid) {
      svg
        .selectAll('.grid')
        .data(y.ticks())
        .join('line')
        .attr('class', 'grid')
        .attr('x1', margin.left)
        .attr('x2', width - margin.right)
        .attr('y1', (d) => y(d))
        .attr('y2', (d) => y(d))
        .attr('stroke', colors.gridLine)
        .attr('stroke-width', 1);
    }

    // X Axis (only first and last tick)
    if (this.showXaxis) {
      const xDomain = x.domain();
      // const xAxis = d3.axisBottom(x).tickValues([xDomain[0], xDomain[1]])
      // const xAxis = d3.axisBottom(x).tickValues([xDomain[0], xDomain[1]]);

      const uniqueTicks =
        xDomain[0].getTime() === xDomain[1].getTime()
          ? [xDomain[0]] // only one unique date
          : [xDomain[0], xDomain[1]];

      const xAxis = d3
        .axisBottom(x)
        .tickFormat((d: any) => formatDate(d))
        .tickValues(uniqueTicks);

      const xAxisGroup = svg
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0, ${height - 50})`)
        .call(xAxis)
        .call((g) => {
          g.selectAll('.tick text')
            .attr('text-anchor', (d: any, i) => {
              const pos = x(d);
              const mid = (margin.left + width - margin.right) / 2;
              return pos < mid || i == 0 ? 'end' : 'start';
            })
            .attr('dx', (d: any, i) => {
              const pos = x(d);
              const mid = (margin.left + width - margin.right) / 2;
              return i == 0 ? '4rem' : '-4rem';
            });

          // ✅ Remove any leftover grid lines
        });

      xAxisGroup
        .selectAll('text')
        .style('fill', colors.axisText)
        .style('font-size', '12px')
        .style('font-weight', '500');

      xAxisGroup
        .select('path.domain')
        .attr('stroke', colors.axisLine)
        .attr('stroke-width', 2)
        .attr('d', `M${30},0H${width - 30}`);

      xAxisGroup.call((g) => {
        g.selectAll('.tick line').remove(); // ✅ Remove tick lines
        g.selectAll('line').remove();
      });
    }

    // Y Axis
    const filteredTicks = this.filterTicks(yMax, step);
    const yAxis = this.yLabels
      ? d3.axisLeft(y).tickValues(filteredTicks).tickFormat(this.yTickFormat as any)
      : d3.axisLeft(y).ticks(0);

    svg
      .append('g')
      .call(yAxis)
      .call((g) => g.select('path').remove())
      .call((g) => g.selectAll('.tick line').remove())
      .call((g) => g.selectAll('.tick text').style('fill', colors.yAxisText));

    // Lines and dots
    for (let i = data.length - 1; i >= 0; i--) {
      const set = data[i].map((d, idx) => ({
        ...d,
        start_time: mainData[idx]?.start_time || d.start_time,
      }));

      const line = d3
        .line<any>()
        .x((d) => x(d.start_time))
        .y((d) => y(d.value * 0.85))
        .curve(d3.curveLinear);

      svg
        .append('path')
        .datum(set)
        .attr('fill', 'none')
        .attr('stroke', set[0].color)
        .attr('stroke-width', data.length === 1 || i !== 0 ? 2 : 2)
        .attr('d', line)
        // moving path 30px above base
        .attr('transform', `translate(0, -30)`)
        .attr('class', `line-${i}`);

      if (data.length === 1 && this.circleStroke) {
        svg
          .selectAll('circle')
          .data(set)
          .join('circle')
          .attr('cx', (d) => x(d.start_time))
          .attr('cy', (d) => y(d.value))
          .attr('r', 4)
          .attr('fill', colors.circleFill)
          .attr('stroke', colors.circleStroke)
          .attr('stroke-width', 3);
      }
    }

    // Tooltip and hover
    const tooltip = d3
      .select(container)
      .append('div')
      .attr('class', 'tooltip py-2 px-3 font-base')
      .style('opacity', '0')
      .style('position', 'absolute')
      .style('background-color', colors.tooltipBg)
      .style('box-shadow', `${colors.tooltipShadow} 0px 0px 11px 0px`)
      .style('border', `1px solid ${colors.tooltipBorder}`)
      // .style("padding", "5px 10px")
      .style('border-radius', '6px')
      .style('font-size', '12px')
      .style('font-weight', '500')
      .style('color', colors.tooltipText)
      .style('pointer-events', 'none')
      .style('flex-direction', 'column')
      .style('gap', '2px');

    const verticalGridLines = svg
      .append('g')
      .attr('class', 'vertical-grid-lines');

    svg
      .selectAll('.hover-rect')
      .data(mainData)
      .join('rect')
      .attr('class', 'hover-rect')
      .attr('x', (d) => x(d.start_time) - 20)
      .attr('y', margin.top)
      .attr('width', '100%')
      .attr('height', height - margin.top - margin.bottom)
      .attr('fill', 'transparent')
      .on('mouseover', (event, d) => {
        const index = mainData.indexOf(d);
        const sets = data.map((ds) => ds[index]).filter(Boolean);
        const percentChange = this.calculatePercentChange(sets);

        svg
          .selectAll('.tick text')
          .style('transition', 'opacity 200ms ease')
          .style('opacity', 0);

        tooltip
          .style('opacity', '1')
          .html(this.generateTooltipHTML(sets, percentChange));

        verticalGridLines
          .selectAll('.grid-line')
          .data([d])
          .join('line')
          .attr('class', 'grid-line')
          .attr('x1', x(d.start_time))
          .attr('x2', x(d.start_time))
          .attr('y1', margin.top)
          .attr('y2', height - 50)
          .attr('stroke', colors.hoverLine)
          .attr('stroke-width', 0.5)
          .attr('stroke-dasharray', '0, 0');

        const label = svg
          .selectAll('.hover-label')
          .data([d])
          .join('text')
          .attr('class', 'hover-label')
          .attr('x', x(d.start_time))
          // .attr("y", height - margin.bottom + 5)
          .attr('y', height - 30)
          .attr('text-anchor', 'middle')
          .attr('font-size', 8)
          .attr('fill', colors.yAxisText)
          .text(d.label);

        const labelBBox = (label.node() as SVGGraphicsElement)?.getBBox();
        const labelX = Math.min(
          Math.max(x(d.start_time), margin.left + labelBBox.width / 2),
          width - margin.right - labelBBox.width / 2
        );
        label.attr('x', labelX);
      })
      .on('mousemove', (event) => {
        const [xCoord, yCoord] = d3.pointer(event, container);
        const tooltipWidth =
          tooltip.node()?.getBoundingClientRect().width || 100;
        const containerWidth = container.clientWidth;

        const tooltipLeft =
          xCoord + 10 + tooltipWidth > containerWidth
            ? xCoord - tooltipWidth - 10
            : xCoord + 10;

        tooltip
          .style('left', `${tooltipLeft}px`)
          .style('top', `${yCoord - 10}px`);
      })
      .on('mouseout', () => {
        verticalGridLines.selectAll('.grid-line').remove();
        tooltip.style('opacity', '0');
        svg.selectAll('.hover-label').remove();
        svg
          .selectAll('.tick text')
          .style('transition', 'opacity 200ms ease')
          .style('opacity', 1);
      });

    // Optional: Add ResizeObserver
    let lastWidth = container.clientWidth;
    let lastHeight = container.clientHeight;

    const observer = new ResizeObserver(() => {
      const { clientWidth, clientHeight } = container;
      if (clientWidth !== lastWidth || clientHeight !== lastHeight) {
        lastWidth = clientWidth;
        lastHeight = clientHeight;
        this.drawMultiLineChart(container);
      }
    });

    observer.observe(container);
    this.resizeObv.set(container, observer);
  }

  private getStepSize(max: number): number {
    if (max <= 10) return 1;
    if (max <= 50) return 5;
    if (max <= 100) return 10;
    if (max <= 500) return 50;
    if (max <= 1000) return 100;
    if (max <= 5000) return 500;
    if (max <= 10000) return 1000;
    if (max <= 50000) return 5000;
    if (max <= 100000) return 10000;
    if (max <= 500000) return 50000;
    if (max <= 1000000) return 100000;
    if (max <= 5000000) return 500000;
    if (max <= 10000000) return 1000000;
    return 5000000;
  }

  private filterTicks(max: number, step: number): number[] {
    const raw = d3.range(0, max + 1, step);
    const seen = new Set();
    return raw.filter((v) => {
      const label = this.yTickFormat(v);
      if (seen.has(label)) return false;
      seen.add(label);
      return true;
    });
  }

  private yTickFormat(value: number): string {
    if (value >= 1e9) return Math.round(value / 1e9) + 'B';
    if (value >= 1e6) return Math.round(value / 1e6) + 'M';
    if (value >= 1e3) return Math.round(value / 1e3) + 'k';
    return Math.round(value).toString();
  }

  private getInnerChartContainer(): HTMLElement {
    return this.chartContainer.nativeElement.querySelector('.chart-container');
  }

  formatTimestamp(unit: string) {
    switch (unit) {
      case 'hour':
        return d3.utcFormat('%H:%M');
      case 'day':
        return d3.utcFormat('%d %b');
      case 'week':
        return (date: any) => {
          const start = d3.utcFormat('%d %b')(d3.utcWeek.floor(date));
          const end = d3.utcFormat('%d %b')(
            d3.utcWeek.offset(d3.utcWeek.floor(date), 1)
          );
          return `${start} - ${end}`;
        };
      case 'month':
        return d3.utcFormat('%b %Y');
      case 'quarter':
        return (date: any) =>
          `Q${Math.ceil(
            (date.getUTCMonth() + 1) / 3
          )} ${date.getUTCFullYear()}`;
      case 'year':
        return d3.utcFormat('%Y');
      default:
        return d3.utcFormat('%Y-%m-%d %H:%M:%S');
    }
  }

  indexColors(index: number): string {
    const colors = [
      '#97C9F3',
      '#9BEBED',
      '#DFF3B5',
      '#F3D7BF',
      '#F3B1AD',
      '#AD9BD5',
      '#8DDFAF',
      '#A5CBEF',
      '#B3E6E1',
      '#CBEF9E',
      '#F5CBAE',
      '#F9A6A6',
      '#C3B3E5',
      '#A6EFC1',
    ];
    return colors[index];
  }

  private drawBarChart(container: HTMLElement) {
    // You can create a bar chart here similar to the pie or multiline
    console.warn('Bar chart not implemented yet');
    let lastWidth = container.clientWidth;
    let lastHeight = container.clientHeight;

    const observer = new ResizeObserver(() => {
      const { clientWidth, clientHeight } = container;
      if (clientWidth !== lastWidth || clientHeight !== lastHeight) {
        lastWidth = clientWidth;
        lastHeight = clientHeight;
        this.drawMultiLineChart(container);
      }
    });

    observer.observe(container);
    this.resizeObv.set(container, observer);
  }

  cardsLineColor(index: number): string {
    const lineColors = [
      '#33C9FF',
      '#FFC300',
      '#D433FF',
      '#3385FF',
      '#FF336F',
      '#FF5733',
      '#FF33A6',
      '#33FF57',
      '#3358FF',
      '#FF8C33',
      '#33FFB5',
      '#FF5733',
      '#6A33FF',
      '#FFD433',
      '#33FFF5',
      '#131921',
    ];
    return !index ? '#1581E5' : lineColors[index % lineColors.length];
  }

  private calculatePercentChange(sets: any[]): number | null {
    if (sets.length !== 2) return null;
    const oldValue = sets[1]?.value || 0;
    const newValue = sets[0]?.value;
    if (oldValue === 0 && newValue === 0) return 0;
    if (oldValue === 0) return 100 * Math.sign(newValue);
    if (newValue === 0) return -100;
    return parseFloat(
      (((newValue - oldValue) / Math.abs(oldValue)) * 100).toFixed(2)
    );
  }

  private generateTooltipHTML(
    sets: any[],
    percentChange: number | null
  ): string {
    let html = '';
    if (percentChange !== null) {
      let color =
        percentChange > 0 ? 'green' : percentChange < 0 ? 'orange' : 'gray';
      html += `
      <div class="flex justify-end items-center pb-1" style="border-bottom: 1px solid #e8e8e8">
        <div class="bg-${color}-100 border border-${color}-200 rounded-md px-2 py-1 text-${color}-700">${percentChange}%</div>
      </div>`;
    }

    sets.forEach((s) => {
      html += this.formatCurrency ? s.tooltip : s.tooltip;
    });

    return html;
  }
  // #CBD1D9
  private createLineChart(element: HTMLElement): void {
    if (this.resizeObv.has(element)) {
      this.resizeObv.get(element)?.disconnect();
      this.resizeObv.delete(element);
    }

    // Get theme colors
    const colors = this.getThemeColors();

    d3.select(element).selectAll('*').remove();

    let width = element?.getBoundingClientRect().width || 928;

    if (!this.chartData || this.chartData.length === 0) {
      return;
    }

    const marginTop = 30;
    const marginRight = 30;
    const marginBottom = 30;
    let marginLeft = 100;

    // Calculate max label width
    const dummySvg = d3
      .select(document.body)
      .append('svg')
      .attr('visibility', 'hidden')
      .style('position', 'absolute');

    let maxLabelWidth = 0;
    
    this.chartData.forEach((d: any, i: number) => {
      const labelText = `${i + 1}. ${d.label || ''}`;
      const capitalizedText = labelText.charAt(0).toUpperCase() + labelText.slice(1);
      
      const tempText = dummySvg
        .append('text')
        .style('font-size', '14px')
        .style('font-weight', '500')
        .text(capitalizedText);
      
      const width = tempText.node()?.getBBox().width || 0;
      if (width > maxLabelWidth) {
        maxLabelWidth = width;
      }
      tempText.remove();
    });

    dummySvg.remove();

    // Cap the margin left to 350 so the bar chart always has enough room
    marginLeft = Math.min(Math.max(maxLabelWidth + 20, 265), 350);

    const barPadding = 0.3;
    const rowHeight = 40;
    const barHeight = 12;
    const rowCount = this.chartData.length;
    const height = marginTop + marginBottom + rowCount * rowHeight;

    const svg = d3
      .select(element)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', [0, 0, width, height])
      .attr('style', 'max-width: 100%; height: auto;');

    // Drop shadow once
    svg
      .append('defs')
      .append('filter')
      .attr('id', 'drop-shadow')
      .attr('height', '130%').html(`
      <feDropShadow dx="0" dy="1" stdDeviation="2" flood-color="#000" flood-opacity="${colors.barLabelShadow === 'rgba(0, 0, 0, 0.3)' ? '0.3' : '0.15'}"/>
    `);

    const color = d3
      .scaleOrdinal<string>()
      .domain(['frequencyA', 'frequencyB'])
      .range([colors.barCurrentPeriod, colors.barPreviousPeriod]);

    const y = d3
      .scaleBand()
      .domain(this.chartData.map((d: any, i) => `${i + 1}. ${d.label}`))
      .range([marginTop, height - marginBottom])
      .paddingInner(barPadding);

    const tooltip = d3
      .select(element)
      .append('div')
      .style('position', 'absolute')
      .style('background', colors.tooltipBg)
      .style('border', `1px solid ${colors.tooltipBorder}`)
      .style('padding', '4px 8px')
      .style('font-size', '12px')
      .style('border-radius', '4px')
      .style('pointer-events', 'none')
      .style('opacity', 0)
      .style('color', colors.tooltipText)
      .style('box-shadow', `0 2px 6px ${colors.tooltipShadow}`);

    // Helper to draw the popup
    function DrawLabel(
      group: d3.Selection<SVGGElement, any, any, any>,
      value: number,
      xCenter: number,
      yTop: number
    ) {
      const labelText = String(value);
      const labelWidth = labelText.length * 9 + 16;
      const labelHeight = 20;
      const labelYPadding = 2; // Added y-padding for better spacing
      const arrowHeight = 6;
      const cornerRadius = 6;
      const labelX = xCenter - labelWidth / 2;

      const labelGroup = group.append('g').attr('class', 'value-label');

      labelGroup
        .append('rect')
        .attr('x', labelX)
        .attr('y', yTop - labelYPadding)
        .attr('width', labelWidth)
        .attr('height', labelHeight + (labelYPadding * 2))
        .attr('rx', cornerRadius)
        .attr('fill', colors.barLabelBg)
        .attr('filter', 'url(#drop-shadow)');

      labelGroup
        .append('text')
        .attr('x', xCenter)
        .attr('y', yTop + labelHeight / 2 + 5)
        .attr('text-anchor', 'middle')
        .attr('fill', colors.barLabelText)
        .attr('font-size', '18px')
        .attr('font-weight', '600')
        .text(labelText);

      labelGroup
        .append('polygon')
        .attr(
          'points',
          `
        ${xCenter - 6},${yTop + labelHeight + labelYPadding}
        ${xCenter + 6},${yTop + labelHeight + labelYPadding}
        ${xCenter},${yTop + labelHeight + labelYPadding + arrowHeight}
      `
        )
        .attr('fill', colors.barLabelBg)
        .attr('filter', 'url(#drop-shadow)');
    }

    // Bars and labels
    svg
      .append('g')
      .attr('class', 'bars')
      .selectAll('g')
      .data(this.chartData)
      .join('g')
      .attr(
        'transform',
        (d: any, i) => `translate(0,${y(`${i + 1}. ${d.label}`)})`
      )
      .each(function (d: any) {
        const group = d3.select(this);
        const yOffset = (y.bandwidth() - barHeight) / 2;

        const total =
          Number(d.frequencyA || 0) + Number(d.frequencyB || 0) || 1;
        // Clamp to 0 so a narrow container (where marginLeft + marginRight > width)
        // can never produce a negative usable width, which would cascade into
        // negative <rect> width attributes and a SVG render error.
        const chartUsableWidth = Math.max(0, width - marginLeft - marginRight);

        const percentA = (d.frequencyA || 0) / total;
        const percentB = (d.frequencyB || 0) / total;

        const rawWidthA = chartUsableWidth * percentA;
        const rawWidthB = chartUsableWidth * percentB;
        const widthA = Math.max(0, rawWidthA);
        const widthB = Math.max(0, rawWidthB);

        // 3px visual gap between the two stacked bars — only applied when
        // BOTH bars actually render. Previously the gap was applied whenever
        // `widthB > 0`, which made `widthA - 3` evaluate to -3 (and emit a
        // SVG `<rect> attribute width: A negative value is not valid ('-3')`
        // error) whenever frequencyA was 0 but frequencyB had any value.
        const barGap = widthA > 0 && widthB > 0 ? 3 : 0;

        const startX = marginLeft;

        // Current Period
        group
          .append('rect')
          .attr('x', startX + widthB + barGap)
          .attr('y', yOffset)
          .attr(
            'width',
            widthA == 0 && widthB == 0
              ? chartUsableWidth
              : Math.max(0, widthA - barGap)
          )
          .attr('height', barHeight)
          .attr(
            'fill',
            widthA == 0 && widthB == 0 ? colors.barEmpty : color('frequencyA')
          )
          .on('mouseover', function () {
            tooltip.transition().duration(200).style('opacity', 0.9);
            tooltip.html(`
              ${
                d.currentPeriod &&
                `<strong>Current Period: ${d.currentPeriod}</strong> <br/>`
              }Current Period: ${d.frequencyA || 0}`);
          })
          .on('mousemove', function (event) {
            // tooltip
            //   .style('left', `${event.offsetX + 10}px`)
            //   .style('top', `${event.offsetY - 20}px`);

            const [ptrX, ptrY] = d3.pointer(event);
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const tooltipHeight = tooltipNode.getBoundingClientRect().height;

            const horizontalOffset = 15;
            const verticalOffset = 5;

            let tooltipLeft = ptrX + horizontalOffset;
            if (tooltipLeft + tooltipWidth > width) {
              tooltipLeft = ptrX - horizontalOffset - tooltipWidth;
            }

            let tooltipTop = ptrY - verticalOffset - tooltipHeight;

            if (tooltipTop < 0) {
              tooltipTop = 0;
            }

            tooltip
              .style('left', `${tooltipLeft}px`)
              .style('top', `${event.offsetY}px`);
          })
          .on('mouseout', () =>
            tooltip.transition().duration(200).style('opacity', 0)
          );

        // Previous Period
        group
          .append('rect')
          .attr('x', startX)
          .attr('y', yOffset)
          .attr('width', Math.max(0, widthB))
          .attr('height', barHeight)
          .attr('fill', color('frequencyB'))
          .on('mouseover', function () {
            tooltip.transition().duration(200).style('opacity', 0.9);
            tooltip.html(
              `${
                d.previousPeriod &&
                `<strong>Previous Period: ${d.previousPeriod}</strong> <br/>`
              }Previous Period: ${d.frequencyB || 0}`
            );
          })
          .on('mouseout', () => {
            tooltip.transition().duration(200).style('opacity', 0);
          })
          .on('mousemove', function (event) {
            const [ptrX, ptrY] = d3.pointer(event);
            const tooltipNode = tooltip.node() as HTMLElement;
            const tooltipWidth = tooltipNode.getBoundingClientRect().width;
            const tooltipHeight = tooltipNode.getBoundingClientRect().height;

            const horizontalOffset = 15;
            const verticalOffset = 5;

            let tooltipLeft = ptrX + horizontalOffset;
            if (tooltipLeft + tooltipWidth > width) {
              tooltipLeft = ptrX - horizontalOffset - tooltipWidth;
            }

            let tooltipTop = ptrY - verticalOffset - tooltipHeight;

            if (tooltipTop < 0) {
              tooltipTop = 0;
            }

            tooltip
              .style('left', `${tooltipLeft}px`)
              .style('top', `${event.offsetY}px`);
          });

        const labelY = yOffset - 20;
        if (d.frequencyB > 0) {
          DrawLabel(group as any, d.frequencyB, startX + widthB / 2, labelY);
        }
        if (d.frequencyA > 0 || (widthA == 0 && widthB == 0)) {
          DrawLabel(
            group as any,
            d.frequencyA,
            widthA == 0 && widthB == 0
              ? (chartUsableWidth * 1.50) / 2
              : startX + widthB + widthA / 2,
            labelY
          );
        }
      });

    // Y Axis
    svg
      .append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${marginLeft},0)`)
      .call(d3.axisLeft(y).tickSizeOuter(0))
      .call((g) => {
        g.selectAll('.tick line').remove();

        // 👇 This is the vertical line of the Y-axis — change its color here
        g.select('path')
          .attr('stroke', colors.axisLine) // 🎨 Change to your desired color here
          .attr('stroke-width', 1); // Optional: Make it thicker if you want

        g.selectAll('.tick text')
          .attr('text-anchor', 'start')
          .attr('x', -marginLeft + 5)
          .attr('dy', '0.32em')
          .style('font-size', '14px')
          .style('font-weight', '500')
          .style('fill', colors.yAxisText)
          .each(function(d: any) {
            const str = d.charAt(0).toUpperCase() + d.slice(1);
            const textNode = d3.select(this);
            textNode.text(str);
            
            // Truncate based on actual available width
            const maxWidth = marginLeft - 15; // 10px padding from axis + 5px starting offset
            let textLength = (textNode.node() as SVGTextElement)?.getComputedTextLength() || 0;
            
            if (textLength > maxWidth) {
              let i = str.length;
              while (textLength > maxWidth && i > 0) {
                textNode.text(str.substring(0, i) + '...');
                textLength = (textNode.node() as SVGTextElement)?.getComputedTextLength() || 0;
                i--;
              }
              // Append title for tooltip on hover
              textNode.append("title").text(str);
            }
          });
      });

    // Define the x scale based on your chart width and data
    const x = d3
      .scaleLinear()
      .domain([
        0,
        d3.max(
          this.chartData,
          (d: any) => (d.frequencyA || 0) + (d.frequencyB || 0)
        ) || 100,
      ]) // max total freq or default
      .range([marginLeft, width - marginRight]);

    // Add X-axis at the bottom
    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height - 29})`) // position at bottom
      .call(d3.axisBottom(x).ticks(5).tickSizeOuter(0))
      .call((g) => {
        g.selectAll('.tick line').remove();
        // .attr('stroke', '#ccc'); // color for ticks

        g.select('path')
          .attr('stroke', colors.axisLine) // 🎨 Change to your desired color here
          .attr('stroke-width', 1); // Optional: Make it thicker if you want

        g.selectAll('text').remove();
        // .style('font-size', '12px')
        // .style('fill', '#131921');
      });

    // Responsive
    let lastWidth = element.clientWidth;
    let lastHeight = element.clientHeight;
    const observer = new ResizeObserver(() => {
      const { clientWidth, clientHeight } = element;
      if (clientWidth !== lastWidth || clientHeight !== lastHeight) {
        lastWidth = clientWidth;
        lastHeight = clientHeight;
        this.createLineChart(element);
      }
    });

    observer.observe(element);
    this.resizeObv.set(element, observer);
  }
}
